import { ICreditMemoJobDetails, IInvoiceJobDetails } from "~/utils/interface/jobDetails";
import styles from "./CreditMemo.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { handleGetListCreditMemoJobDetails } from "~/thunks/job-details/jobDetailsThunk";
import { notify } from "~/utils/helper";
import dayjs from "dayjs";
import { ActionIcon } from "~/assets/icons";
import { ArrowDown, SearchIcon } from "lucide-react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi from "~/apis/jobDetails";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";

const cx = classNames.bind(styles);

interface CreditMemoProps {
  jobId: string;
  handleCreateCreditMemo: (listCreditMemo: ICreditMemoJobDetails[]) => void;
  getListCreditMemo: boolean;
  setGetListCreditMemo: (value: boolean) => void;
  handleEditCreditMemo: (idCreditMemo: string) => void;
}

export const CreditMemoStatus = {
  DRAFT: "Draft",
  SENT: "Sent",
  OPEN: "Open",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
  VOID: "Void",
};

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Draft", value: "Draft" },
  { label: "Sent", value: "Sent" },
  { label: "Open", value: "Open" },
  { label: "Closed", value: "Closed" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "Void", value: "Void" },
];

const CreditMemo = ({
  jobId,
  handleCreateCreditMemo,
  getListCreditMemo,
  setGetListCreditMemo,
  handleEditCreditMemo,
}: CreditMemoProps) => {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { creditMemoJobDetailsList } = useAppSelector(
    (state) => state.jobDetails
  );
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const activeActionButtonRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const creditMemoTableRef = useRef<HTMLDivElement>(null);
  const [openModalAlertDelete, setOpenModalAlertDelete] = useState(false);
  const [creditMemoId, setCreditMemoId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPageOptions = [10, 20, 50, 100];


  const [formData, setFormData] = useState({
    status: "",
  });


  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

  };

  useEffect(() => {
    if (jobId || getListCreditMemo === true) {
      setIsLoading(true);
      dispatch(
        handleGetListCreditMemoJobDetails({
          idJob: jobId,
        })
      )
        .unwrap()
        .then((res) => {
          setIsLoading(false);
          setGetListCreditMemo(false);
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to fetch credit memo list: ${error.response.data.message}`
          );
          setIsLoading(false);
          setGetListCreditMemo(false);
        });
    }
  }, [dispatch, jobId, getListCreditMemo, setGetListCreditMemo]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  const calculateActionMenuPosition = (buttonElement: HTMLDivElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const menuWidth = 123;
    const menuHeight = 102;

    const spacing = 5;
    const padding = 8;

    const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
    const spaceAbove = rect.top - spacing - padding;

    let top: number;
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      top = rect.top - menuHeight - spacing;
      if (top < padding) {
        top = padding;
      }
    } else {
      top = rect.bottom + spacing;
      if (top + menuHeight > viewportHeight - padding) {
        if (spaceAbove > menuHeight) {
          top = rect.top - menuHeight - spacing;
        } else {
          top = Math.max(padding, viewportHeight - menuHeight - padding);
        }
      }
    }

    let left = rect.right - menuWidth;

    if (left < padding) {
      left = rect.left;
    }

    if (left + menuWidth > viewportWidth - padding) {
      left = Math.max(padding, viewportWidth - menuWidth - padding);
    }

    return {
      top,
      left,
    };
  };

  useEffect(() => {
    const handleScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleTableScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleResize = () => {
      if (activeMenu && activeActionButtonRef.current) {
        requestAnimationFrame(() => {
          const position = calculateActionMenuPosition(
            activeActionButtonRef.current!
          );
          setMenuPosition(position);
        });
      }
    };

    const tableElement = creditMemoTableRef.current;

    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleResize);

    if (tableElement) {
      tableElement.addEventListener("scroll", handleTableScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", handleResize);
      if (tableElement) {
        tableElement.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [activeMenu]);

  const filteredCreditMemos = useMemo(() => {
    let filtered = creditMemoJobDetailsList?.responses || [];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((creditMemo: ICreditMemoJobDetails) => {
        const number = creditMemo.creditMemoUniqueCode?.toLowerCase() || "";
        const related = "Null";
        const status = creditMemo.status?.toLowerCase() || "";
        const total = creditMemo.total?.toString().toLowerCase() || "";
        const creditMemoDate = creditMemo.creditMemoDate?.toLowerCase() || "";
        const dateUpdated = creditMemo.updatedAt?.toLowerCase() || "";

        return (
          number.includes(query) ||
          related.includes(query) ||
          status.includes(query) ||
          total.includes(query) ||
          creditMemoDate.includes(query) ||
          dateUpdated.includes(query)
        );
      });
    }
    if (formData.status) {
          filtered = filtered.filter(
            (invoice: IInvoiceJobDetails) =>
              (invoice.status || "").toLowerCase() === formData.status.toLowerCase()
          );
        }
    return filtered;
  }, [creditMemoJobDetailsList?.responses, searchQuery]);

  const totalItems = filteredCreditMemos.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleChangePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setShowItemsDropdown(false);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }

    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  const paginatedCreditMemos = useMemo(() => {
    return filteredCreditMemos.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredCreditMemos, page, itemsPerPage]);

  const showingCount = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const remaining = Math.max(totalItems - startIndex, 0);
    return Math.min(itemsPerPage, remaining);
  }, [page, itemsPerPage, totalItems]);

  const handleActionClick = (
    event: React.MouseEvent,
    creditMemo: ICreditMemoJobDetails
  ) => {
    event.stopPropagation();

    if (activeMenu === creditMemo.id) {
      setActiveMenu(null);
      activeActionButtonRef.current = null;
      return;
    }

    const buttonElement = event.currentTarget as HTMLDivElement;
    activeActionButtonRef.current = buttonElement;
    const position = calculateActionMenuPosition(buttonElement);
    setMenuPosition(position);
    setActiveMenu(creditMemo.id);
  };

  const ActionMenu = ({ creditMemo, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleEdit = () => {
      onClose();
      handleEditCreditMemo(creditMemo.id);
    };

    const handleDelete = () => {
      setOpenModalAlertDelete(true);
      setCreditMemoId(creditMemo.id);
      onClose();
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <button className={cx("menu-item")} onClick={handleEdit}>
          Edit
        </button>

        <button className={cx("menu-item")} onClick={handleDelete}>
          Delete
        </button>
      </div>
    );
  };

  const handleDeleteCreditMemo = async () => {
    try {
      setIsLoading(true);
      await jobDetailsApi.deleteCreditMemo(jobId, creditMemoId || "");
      setIsLoading(false);
      notify("success", "Delete credit memo successfully");
      setOpenModalAlertDelete(false);
      setCreditMemoId(null);
      setGetListCreditMemo(true);
    } catch (error: any) {
      notify("error", error.response?.data?.message || "Failed to delete credit memo");
      setIsLoading(false);
    }
  };

  return (
    <div className={cx("credit-memo-container")}>
      <div className={cx("credit-memo-header")}>
        <div className={cx("search-container")}>
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder="Search Credit Memo"
            className={cx("search-input")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className={cx("filter-container")}>
          <PrimarySelectInput
            name="filter"
            placeholder="Select"
            value={formData.status}
            onChange={(value) => handleFieldChange("status", value)}
            options={STATUS_OPTIONS.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            className={cx("input")}
            classNameIcon={cx("select-input-icon")}
          />

        <PrimaryButton
          title="Create Credit Memo"
          className={cx("create-button")}
          onClick={() => handleCreateCreditMemo(creditMemoJobDetailsList?.responses || [])}
        />
        </div>
      </div>

      <div ref={creditMemoTableRef} className={cx("credit-memo-table")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <>
            <div className={cx("table-wrapper")}>
              <table className={cx("sections-table")}>
                <thead>
                  <tr>
                    <th className={cx("col-number")}>Number</th>
                    <th className={cx("col-related")}>Related</th>
                    <th className={cx("col-status")}>Status</th>
                    <th className={cx("col-total")}>Total</th>
                    <th className={cx("col-credit-memo-date")}>Credit Memo Date</th>
                    <th className={cx("col-date-updated")}>Date Updated</th>
                    <th className={cx("col-actions")}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCreditMemos.map((creditMemo: ICreditMemoJobDetails) => (
                    <tr key={creditMemo.id} className={cx("item-row")}>
                      <td className={cx("col-number")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {creditMemo.creditMemoUniqueCode || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-related")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {creditMemo.job?.jobUniqueID || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-status")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {CreditMemoStatus[
                              creditMemo.status as keyof typeof CreditMemoStatus
                            ] || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-total")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            ${creditMemo.total || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-credit-memo-date")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {creditMemo.creditMemoDate
                              ? dayjs(creditMemo.creditMemoDate).format(
                                  "DD MMM ‘YYYY"
                                )
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-date-updated")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {creditMemo.updatedAt
                              ? dayjs(creditMemo.updatedAt).format("DD MMM ‘YYYY")
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-actions")}>
                        <div
                          className={cx("action-icon-container")}
                          onClick={(e) => handleActionClick(e, creditMemo)}
                        >
                          <div>
                            <ActionIcon className={cx("action-icon")} />
                          </div>
                          {activeMenu === creditMemo.id && (
                            <ActionMenu
                              creditMemo={creditMemo}
                              onClose={() => {
                                setActiveMenu(null);
                                activeActionButtonRef.current = null;
                              }}
                              position={menuPosition}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={cx("pagination-container")}>
              <div className={cx("pagination-left")}>
                <span className={cx("pagination-text")}>Show result</span>
                <div className={cx("items-per-page-wrapper")} ref={dropdownRef}>
                  <button
                    className={cx("items-per-page-button")}
                    onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                  >
                    <span>{showingCount}</span>
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 9 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
                        fill="#789EB4"
                      />
                    </svg>
                  </button>
                  {showItemsDropdown && (
                    <div className={cx("items-per-page-dropdown")}>
                      {itemsPerPageOptions.map((option) => (
                        <button
                          key={option}
                          className={cx("dropdown-item", {
                            active: itemsPerPage === option,
                          })}
                          onClick={() => handleItemsPerPageChange(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className={cx("pagination-text")}>out of</span>
                <span className={cx("pagination-total")}>{totalItems}</span>
              </div>

              <div className={cx("pagination-right")}>
                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === 1 || page === 0,
                  })}
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 1 || page === 0}
                >
                  <ArrowDown className={cx("nav-icon", "rotate-90")} />
                  <span>Previous</span>
                </button>

                <div className={cx("page-numbers")}>
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className={cx("page-ellipsis")}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNumber = Number(pageNum);
                    const isActive = page === pageNumber;
                    return (
                      <button
                        key={pageNumber}
                        className={cx("page-number", {
                          active: isActive,
                        })}
                        onClick={() => handleChangePage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === totalPages,
                  })}
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  <span>Next</span>
                  <ArrowDown className={cx("nav-icon", "!rotate-[270deg]")} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ModalAlertDelete
        open={openModalAlertDelete}
        description="Are you sure you want to delete this credit memo?"
        onCancel={() => setOpenModalAlertDelete(false)}
        onConfirm={handleDeleteCreditMemo}
      />
    </div>
  );
};

export default CreditMemo;
